import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import StoryEditor from './StoryEditor';
import type {
  BilingualLine,
  EpisodeSetupResponse,
  MediaItem,
  NarrationStatus,
  SetupSuggestions,
  Story,
  StoryLanguage,
  StoryMood,
  StorySetup,
} from '../types';
import DownloadVideoButton from './DownloadVideoButton';
import { StoryPrepOverlay, readyProblems, type PrepStage } from './StoryPrepOverlay';
import { clipLengths, primaryLanguage } from '../player/voices';
import { labelOf, narrationSnapshot, onNarrationChange, playClip, stopClip } from '../player/narrationAudio';
import type { PersonRef } from '../types';
import { StoryAnswerFields, answersOf, answersPatch, storyLanguageFor, type StoryAnswers } from './create/QuestionsStep';
import { MusicLookFields, useMusicLook } from './create/StyleStep';
import { CATEGORY_LABELS } from '../player/types';

/**
 * One story line in the chosen language. Urdu is written in Roman letters,
 * so it reads left to right like English.
 */
export function StoryText({
  line,
  language,
  className = '',
  urClassName = '',
}: {
  line: BilingualLine;
  language: StoryLanguage;
  className?: string;
  urClassName?: string;
}) {
  // Mixed stories keep one sentence in both fields: show it once.
  if (line.en && line.en === line.ur) {
    return (
      <p lang="ur-Latn" className={`leading-relaxed ${urClassName || className}`}>
        {line.ur}
      </p>
    );
  }
  const showEn = language !== 'ur' && line.en;
  const showUr = language !== 'en' && line.ur;
  return (
    <>
      {showEn && <p className={className}>{line.en}</p>}
      {showUr && (
        <p lang="ur-Latn" className={`leading-relaxed ${urClassName || className}`}>
          {line.ur}
        </p>
      )}
    </>
  );
}

const LANGUAGE_OPTIONS: Array<{ value: 'en' | 'ur'; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ur', label: 'Urdu' },
];

/** Plays one part of the story in one language (the audio preview). */
function ListenButton({
  episodeId,
  story,
  status,
  lang,
  part,
  mediaId,
}: {
  episodeId: string;
  story: Story;
  status: NarrationStatus | null;
  lang: 'en' | 'ur';
  part: 'opening' | 'closing' | 'line';
  mediaId?: string;
}) {
  const [, setTick] = useState(0);
  useEffect(() => onNarrationChange(() => setTick((t) => t + 1)), []);
  const snap = narrationSnapshot();
  const label = labelOf({ part, mediaId });
  const me = snap.current === label && snap.lang === lang;
  const busy = me && (snap.state === 'requested' || snap.state === 'playing');
  const lengths = clipLengths(status, story, lang, null);
  const name = lang === 'en' ? 'English' : 'Urdu';
  return (
    <button
      type="button"
      onClick={() =>
        busy
          ? stopClip()
          : playClip({ episodeId, part, mediaId, lang, voice: lengths.voice, version: lengths.version(part, mediaId) })
      }
      aria-label={busy ? 'Stop' : `Listen in ${name}`}
      title={busy ? 'Stop' : `Listen in ${name}`}
      className="focus-ring grid h-10 w-10 flex-none place-items-center rounded-full text-white/55 hover:bg-white/10 hover:text-white"
    >
      {busy ? (
        snap.state === 'requested' ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" aria-hidden="true" />
        ) : (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
            <path d="M6 6h12v12H6z" />
          </svg>
        )
      ) : (
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M4 10v4h4l5 4V6L8 10H4z" strokeLinejoin="round" />
          <path d="M16 9a4 4 0 010 6M18.5 6.5a7.5 7.5 0 010 11" strokeLinecap="round" />
        </svg>
      )}
      {me && snap.state === 'error' && <span className="sr-only">Could not play</span>}
    </button>
  );
}

interface Props {
  episodeId: string;
  items: MediaItem[];
  story: Story | null | undefined;
  /** For the "Getting things ready" screen: the film's opening title over the cover. */
  title: string;
  backdropUrl: string | null;
  dateLine: string | null;
  /** The mood the family chose for the story (asked before it is written). */
  mood?: StoryMood | null;
  storyLanguage?: 'en' | 'ur' | 'mix' | null;
  voiceGender?: 'male' | 'female' | null;
  narrator?: { kind: 'neutral' | 'person'; personId?: string } | null;
  /** For the finished-look previews. */
  coverUrl?: string | null;
  onStory: (story: Story | null) => void;
  /** Contributor or more: make, edit, write again, remove. Otherwise read and preview only. */
  canEdit?: boolean;
  /** Owner only. */
  canDownload?: boolean;
}

/**
 * "Make it a story": the AI writes an opening, a line for each key memory and
 * a closing, in English and Urdu. The player shows them as subtitles.
 */
const VOICE_OPTIONS: Array<{ value: 'male' | 'female'; label: string }> = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];


export default function StoryPanel({
  episodeId,
  items,
  story,
  title,
  backdropUrl,
  dateLine,
  mood: savedMood,
  storyLanguage: savedStoryLanguage,
  voiceGender: savedVoiceGender,
  narrator: savedNarrator,
  coverUrl = null,
  onStory,
  canEdit = true,
  canDownload = true,
}: Props) {
  const navigate = useNavigate();
  // The same answers as the create wizard, saved on the episode (prefilled once loaded).
  const [setupData, setSetupData] = useState<EpisodeSetupResponse | null>(null);
  const [answers, setAnswers] = useState<StoryAnswers>(() =>
    answersOf({
      occasion: null,
      occasionOther: null,
      about: null,
      mood: savedMood ?? null,
      storyLanguage: savedStoryLanguage ?? null,
      language: null,
      length: null,
      music: null,
      look: null,
      completedAt: null,
    }),
  );
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const mood = answers.mood;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [voiceGender, setVoiceGender] = useState<'male' | 'female' | null>(savedVoiceGender ?? null);
  // Who tells it: 'neutral' or a person's id. Named people come from People.
  const [narratorKey, setNarratorKey] = useState<string | null>(
    savedNarrator ? (savedNarrator.kind === 'neutral' ? 'neutral' : (savedNarrator.personId ?? null)) : null,
  );
  const [named, setNamed] = useState<PersonRef[]>([]);
  const [progressStart, setProgressStart] = useState(0);
  const [tick, setTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'rewrite' | 'remove' | null>(null);
  const [editing, setEditing] = useState(false);
  // Both languages are always there: the switch shows one, starting with the story's first language.
  const [lang, setLang] = useState<'en' | 'ur'>(() => primaryLanguage(story));
  useEffect(() => setLang(primaryLanguage(story)), [story?.generatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const sectionRef = useRef<HTMLElement>(null);

  // ---- Narration in both languages: status, and the "Getting things ready" screen ----
  const [narration, setNarration] = useState<NarrationStatus | null>(null);
  const [prep, setPrep] = useState<{ stage: PrepStage; error: string | null; problems: string[] } | null>(null);
  const prepRun = useRef(0);
  const refreshNarration = useCallback(() => {
    api.narrationStatus(episodeId).then(setNarration).catch(() => setNarration(null));
  }, [episodeId]);
  useEffect(() => {
    if (story) refreshNarration();
    else setNarration(null);
  }, [story, refreshNarration]);
  useEffect(() => () => stopClip(), []);

  /** Make both narrations, wait for them, then run the checks. */
  const prepare = async (forStory: Story) => {
    const run = ++prepRun.current;
    setPrep({ stage: 'audio', error: null, problems: [] });
    try {
      let st = await api.prepareNarration(episodeId);
      while (run === prepRun.current && (st.state === 'running' || st.clips.some((c) => c.status === 'making'))) {
        setNarration(st);
        await new Promise((r) => window.setTimeout(r, 1200));
        st = await api.narrationStatus(episodeId);
      }
      if (run !== prepRun.current) return;
      setNarration(st);
      setPrep({ stage: 'checking', error: null, problems: [] });
      const problems = readyProblems(forStory, st, items);
      setPrep({ stage: problems.length ? 'failed' : 'ready', error: null, problems });
    } catch (err) {
      if (run === prepRun.current) setPrep({ stage: 'failed', error: (err as Error).message, problems: [] });
    }
  };
  const closePrep = () => {
    prepRun.current++;
    setPrep(null);
    refreshNarration();
  };
  const play = () => {
    prepRun.current++;
    navigate(`/play/${episodeId}?lang=${lang}`);
  };

  // Arriving from the create wizard's "Make it a story" link.
  useEffect(() => {
    if (window.location.hash === '#story') sectionRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .story(episodeId)
      .then((r) => {
        if (!cancelled) setEnabled(r.enabled);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    api
      .episodeSetup(episodeId)
      .then((r) => {
        if (cancelled) return;
        setSetupData(r);
        setAnswers(answersOf(r.setup));
        if (r.setup.voiceGender) setVoiceGender(r.setup.voiceGender);
        if (r.setup.narrator)
          setNarratorKey(r.setup.narrator.kind === 'neutral' ? 'neutral' : (r.setup.narrator.personId ?? null));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [episodeId, canEdit]);

  /** Save some answers; the existing story stays as it is until it is written again. */
  const saveSetup = useCallback(
    async (patch: Parameters<typeof api.saveEpisodeSetup>[1]) => {
      try {
        const r = await api.saveEpisodeSetup(episodeId, patch);
        setSetupData((d) => (d ? { ...d, setup: r.setup, suggested: r.suggested } : d));
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [episodeId],
  );
  const changeAnswers = (patch: Partial<StoryAnswers>) => {
    const next = { ...answersRef.current, ...patch };
    answersRef.current = next;
    setAnswers(next);
    // Chips save at once; typed answers save when the field loses focus.
    if (!('about' in patch) && !('occasionOther' in patch)) void saveSetup(answersPatch(next));
  };

  useEffect(() => {
    let cancelled = false;
    api
      .episodePeople(episodeId)
      .then((r) => {
        if (!cancelled) setNamed(r.groups.flatMap((g) => (g.person ? [g.person] : [])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [episodeId]);

  // Progress while the AI writes: elapsed seconds and a message that changes.
  useEffect(() => {
    if (!writing) return;
    setProgressStart(Date.now());
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [writing]);

  const write = async () => {
    if (!mood || !voiceGender || !narratorKey) return;
    setWriting(true);
    setError(null);
    setConfirm(null);
    const run = ++prepRun.current;
    setPrep({ stage: 'writing', error: null, problems: [] });
    let written: Story | null = null;
    try {
      // The server writes the story in the mood saved on the episode.
      await api.saveEpisodeSetup(episodeId, {
        ...answersPatch(answers),
        // Both languages are always written; this is the one shown first (Urdu when never chosen).
        storyLanguage: storyLanguageFor(answers.language) ?? (savedStoryLanguage === 'en' ? 'en' : 'ur'),
        voiceGender,
        narrator: narratorKey === 'neutral' ? { kind: 'neutral' } : { kind: 'person', personId: narratorKey },
      });
      const r = await api.makeStory(episodeId);
      onStory(r.story);
      written = r.story;
    } catch (err) {
      setError((err as Error).message);
      if (run === prepRun.current) setPrep({ stage: 'failed', error: (err as Error).message, problems: [] });
    } finally {
      setWriting(false);
    }
    if (written && run === prepRun.current) await prepare(written);
  };

  /** Tick or untick a photo for the story. Unticked photos lose their line. */
  const toggleIncluded = async (id: string, include: boolean) => {
    const skipped = new Set(story?.skipped ?? []);
    if (include) skipped.delete(id);
    else skipped.add(id);
    setSaving(true);
    setError(null);
    try {
      const r = await api.editStory(episodeId, { skipped: [...skipped] });
      onStory(r.story);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setError(null);
    setConfirm(null);
    try {
      await api.deleteStory(episodeId);
      onStory(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const editedCount = story
    ? story.lines.filter((l) => l.edited).length + (story.opening.edited ? 1 : 0) + (story.closing.edited ? 1 : 0)
    : 0;

  const storyOptions = setupData ? (
    <>
      <StoryAnswerFields
        value={answers}
        onChange={changeAnswers}
        onCommit={() => void saveSetup(answersPatch(answersRef.current))}
        moodLabel="What mood should the story have?"
        idPrefix={`story-${episodeId}`}
      />
      <fieldset className="mb-7">
        <legend className="mb-1 text-sm font-semibold">Narrator voice</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {VOICE_OPTIONS.map((o) => {
            const on = voiceGender === o.value;
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setVoiceGender(o.value);
                  void saveSetup({ voiceGender: o.value });
                }}
                className={`focus-ring rounded-full px-4 py-2 text-sm transition ${
                  on ? 'bg-white font-semibold text-black' : 'bg-white/[0.07] text-white/80 ring-1 ring-white/10 hover:bg-white/[0.12]'
                }`}
              >
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-1 text-sm font-semibold">Who tells the story?</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {[{ key: 'neutral', label: 'A friend', sub: 'the narrator is outside the story' }, ...named.map((p) => ({ key: p.id, label: p.name, sub: 'tells it as themselves' }))].map((o) => {
            const on = narratorKey === o.key;
            return (
              <button
                key={o.key}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setNarratorKey(o.key);
                  void saveSetup({
                    narrator: o.key === 'neutral' ? { kind: 'neutral' } : { kind: 'person', personId: o.key },
                  });
                }}
                className={`focus-ring rounded-full px-4 py-2 text-sm transition ${
                  on ? 'bg-white font-semibold text-black' : 'bg-white/[0.07] text-white/80 ring-1 ring-white/10 hover:bg-white/[0.12]'
                }`}
              >
                {o.label}
                <span className={`ml-1.5 text-xs ${on ? 'text-black/55' : 'text-white/40'}`}>{o.sub}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    </>
  ) : (
    <div aria-busy="true" className="skeleton h-40 w-full" />
  );

  return (
    <section ref={sectionRef} id="story" className="mb-10 max-w-3xl scroll-mt-24" aria-labelledby="story-heading">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 id="story-heading" className="text-sm font-semibold tracking-wide text-white/50 uppercase">
          The story
        </h2>
        {story && !editing && (
          <div className="ml-auto flex rounded-full bg-white/10 p-0.5 text-sm" role="group" aria-label="Story language">
            {LANGUAGE_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => {
                  stopClip();
                  setLang(o.value);
                }}
                aria-pressed={lang === o.value}
                className={`focus-ring min-h-10 rounded-full px-4 py-1.5 font-semibold transition ${
                  lang === o.value ? 'bg-white text-black' : 'text-white/65 hover:text-white'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!story && !canEdit && <p className="text-sm text-white/50">No story for this episode yet.</p>}

      {!story && canEdit && (
        <div className="rounded-xl bg-white/[0.04] p-5 ring-1 ring-white/10">
          <p className="text-base font-semibold">Make it a story</p>
          <p className="mt-1 text-sm text-white/55">
            Answer a few quick questions, then the story is written for you. Mood, voice and who tells it are needed; the rest
            helps.
          </p>
          {enabled !== false && (
            <div className="mt-4">
              {storyOptions}
            </div>
          )}
          {enabled === false ? (
            <p className="mt-4 text-sm text-amber-200/80">
              AI stories are switched off: the server has no AI key. Add ANTHROPIC_API_KEY to .env and restart the server.
            </p>
          ) : (
            <button
              onClick={write}
              disabled={writing || enabled === null || !setupData || items.length === 0 || !mood || !voiceGender || !narratorKey}
              className="focus-ring mt-4 rounded-md bg-white px-5 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-50"
            >
              {writing ? 'Writing your story…' : '✨ Generate story'}
            </button>
          )}
          {writing && (
            <div className="mt-4" aria-live="polite">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full w-1/3 rounded-full bg-gradient-to-r from-transparent via-white to-transparent"
                  style={{ animation: 'story-sweep 1.4s ease-in-out infinite' }}
                />
              </div>
              <p className="mt-2 text-sm text-white/70">
                {[
                  'Looking through the photos…',
                  'Finding the funny bits…',
                  'Writing the lines…',
                  'Making the Urdu sound like home…',
                  'Almost ready…',
                ][Math.floor(Math.max(0, tick - 0) / 4) % 5]}
              </p>
              <p className="mt-1 text-xs text-white/35">
                {Math.round((Date.now() - progressStart) / 1000)} s · usually 20–40 seconds
              </p>
            </div>
          )}
        </div>
      )}

      {story && editing && (
        <StoryEditor
          episodeId={episodeId}
          items={items}
          story={story}
          onCancel={() => setEditing(false)}
          onSaved={(s) => {
            onStory(s);
            setEditing(false);
            refreshNarration();
          }}
        />
      )}

      {story && !editing && (
        <div className="rounded-xl bg-white/[0.04] p-5 ring-1 ring-white/10" aria-busy={writing}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <StoryText line={story.opening} language={lang} className="text-lg font-semibold text-white/90" urClassName="text-xl text-white/90" />
            </div>
            <ListenButton episodeId={episodeId} story={story} status={narration} lang={lang} part="opening" />
          </div>
          <ol className="mt-5 space-y-4" aria-label="Memories in the story">
            {items.map((item) => {
              const l = story.lines.find((x) => x.mediaId === item.id);
              const included = !(story.skipped ?? []).includes(item.id);
              const thumb = item.type === 'VIDEO' ? item.posterUrl : item.thumbnailUrl ?? item.url;
              return (
                <li key={item.id} className={`flex gap-3 ${included ? '' : 'opacity-45'}`}>
                  <label className="flex flex-none cursor-pointer items-start pt-1">
                    <input
                      type="checkbox"
                      checked={included}
                      disabled={saving || writing}
                      onChange={(e) => void toggleIncluded(item.id, e.target.checked)}
                      aria-label={`Use this memory in the story`}
                      className="h-4 w-4 accent-white"
                    />
                  </label>
                  {thumb ? (
                    <img src={thumb} alt="" loading="lazy" className="h-14 w-14 flex-none rounded-md object-cover" />
                  ) : (
                    <div className="h-14 w-14 flex-none rounded-md bg-white/10" />
                  )}
                  <div className="min-w-0 flex-1 text-sm text-white/75">
                    {!included ? (
                      <p className="text-white/40">Left out of the story</p>
                    ) : l ? (
                      <div className="flex items-start gap-1">
                        <div className="min-w-0 flex-1">
                          <StoryText line={l} language={lang} urClassName="text-base text-white/75" />
                          {l.edited && <p className="mt-0.5 text-[11px] text-white/35">✎ Your words</p>}
                        </div>
                        <ListenButton episodeId={episodeId} story={story} status={narration} lang={lang} part="line" mediaId={item.id} />
                      </div>
                    ) : (
                      <p className="text-white/40">Plays without a subtitle</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="mt-5 flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <StoryText line={story.closing} language={lang} className="text-base text-white/70 italic" urClassName="text-lg text-white/70" />
            </div>
            <ListenButton episodeId={episodeId} story={story} status={narration} lang={lang} part="closing" />
          </div>

          {narration && (
            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm" role="status">
              {narration.ready ? (
                <span className="text-emerald-300/90">✓ Narration ready in English and Urdu</span>
              ) : (
                <>
                  <span className="text-amber-200/85">{narration.problems[0] ?? 'Narration is not ready yet.'}</span>
                  {narration.enabled && (
                    <button
                      onClick={() => void prepare(story)}
                      className="focus-ring min-h-10 rounded-md bg-white/15 px-3 py-1.5 text-sm font-semibold hover:bg-white/25"
                    >
                      Get it ready
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {canEdit && enabled !== false && (
            <div className="mt-6 border-t border-white/10 pt-4">
              <button
                type="button"
                aria-expanded={optionsOpen}
                onClick={() => setOptionsOpen((v) => !v)}
                className="focus-ring rounded text-sm font-semibold text-white/70 hover:text-white"
              >
                {optionsOpen ? '▾' : '▸'} Story options
              </button>
              {optionsOpen && (
                <div className="mt-4">
                  <p className="mb-5 text-xs text-white/45">
                    Changes are saved now and used the next time you choose “Write it again”. This story stays as it is until then.
                  </p>
                  {storyOptions}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4 text-sm">
            {confirm === 'rewrite' ? (
              <>
                <span className="text-white/60">
                  {editedCount > 0
                    ? `Write the rest again? Your ${editedCount} edited ${editedCount === 1 ? 'line stays' : 'lines stay'}.`
                    : 'Write a new story? This one will be replaced.'}
                </span>
                <button
                  onClick={write}
                  disabled={!mood || !voiceGender || !narratorKey}
                  className="focus-ring rounded-md bg-white px-4 py-2 font-semibold text-black hover:bg-white/85 disabled:opacity-50"
                >
                  Yes, write again
                </button>
                <button onClick={() => setConfirm(null)} className="focus-ring rounded-md px-3 py-2 text-white/60 hover:text-white">
                  Cancel
                </button>
              </>
            ) : confirm === 'remove' ? (
              <>
                <span className="text-white/60">Remove the whole story, including your own lines?</span>
                <button onClick={remove} className="focus-ring rounded-md bg-red-500/90 px-4 py-2 font-semibold text-white hover:bg-red-500">
                  Remove
                </button>
                <button onClick={() => setConfirm(null)} className="focus-ring rounded-md px-3 py-2 text-white/60 hover:text-white">
                  Cancel
                </button>
              </>
            ) : !canEdit ? (
              <button
                onClick={play}
                className="focus-ring rounded-md bg-white/15 px-4 py-2 font-semibold hover:bg-white/25"
                title={`Plays in ${lang === 'en' ? 'English' : 'Urdu'}, as chosen above`}
              >
                ▶ Preview
              </button>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  disabled={writing}
                  className="focus-ring rounded-md bg-white px-4 py-2 font-semibold text-black hover:bg-white/85 disabled:opacity-50"
                >
                  Edit lines
                </button>
                <button
                  onClick={() => (narration?.ready || narration?.enabled === false ? play() : void prepare(story))}
                  className="focus-ring rounded-md bg-white/15 px-4 py-2 font-semibold hover:bg-white/25"
                  title={`Plays in ${lang === 'en' ? 'English' : 'Urdu'}, as chosen above`}
                >
                  ▶ Preview
                </button>
                <button
                  onClick={() => setConfirm('rewrite')}
                  disabled={writing || enabled === false}
                  className="focus-ring rounded-md bg-white/15 px-4 py-2 font-semibold hover:bg-white/25 disabled:opacity-50"
                >
                  {writing ? 'Writing…' : 'Write it again'}
                </button>
                <button
                  onClick={() => (editedCount > 0 ? setConfirm('remove') : void remove())}
                  disabled={writing}
                  className="focus-ring rounded-md px-3 py-2 text-white/50 hover:text-white disabled:opacity-50"
                >
                  Remove story
                </button>
              </>
            )}
          </div>
          {confirm === null && canDownload && (
            <div className="mt-4 border-t border-white/10 pt-4">
              <DownloadVideoButton episodeId={episodeId} lang={lang} />
            </div>
          )}
        </div>
      )}

      {canEdit && setupData && (
        <MusicLookCard
          key={`${setupData.suggested.musicMood}:${setupData.suggested.look}`}
          setup={setupData.setup}
          suggested={setupData.suggested}
          looks={setupData.looks}
          coverUrl={coverUrl}
          onSave={saveSetup}
        />
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-300">
          {error}
        </p>
      )}

      {prep && (
        <StoryPrepOverlay
          mood={mood}
          title={title}
          backdropUrl={backdropUrl}
          dateLine={dateLine}
          stage={prep.stage}
          status={prep.stage === 'writing' ? null : narration}
          problems={prep.problems}
          error={prep.error}
          onPlay={play}
          onRetry={() => (story ? void prepare(story) : void write())}
          onClose={closePrep}
        />
      )}
    </section>
  );
}

/**
 * Music and finished look, the same choices as the wizard's last step,
 * folded away behind a one-line summary. Each change is saved at once.
 */
function MusicLookCard({
  setup,
  suggested,
  looks,
  coverUrl,
  onSave,
}: {
  setup: StorySetup;
  suggested: SetupSuggestions;
  looks: EpisodeSetupResponse['looks'];
  coverUrl: string | null;
  onSave: (patch: Parameters<typeof api.saveEpisodeSetup>[1]) => Promise<void>;
}) {
  const state = useMusicLook(setup, suggested, looks);
  const [open, setOpen] = useState(false);
  const lastSaved = useRef(0);
  useEffect(() => {
    if (state.edits === lastSaved.current) return;
    lastSaved.current = state.edits;
    void onSave(state.toSave());
  }, [state.edits]); // eslint-disable-line react-hooks/exhaustive-deps

  const music = !state.musicOn
    ? 'No music'
    : `${CATEGORY_LABELS[state.musicMood]}${state.tracks ? ` · ${state.chosen.length} ${state.chosen.length === 1 ? 'song' : 'songs'}` : ''}`;
  const look = state.look.preset === 'custom' ? 'Your own mix' : looks[state.look.preset].label;

  return (
    <div className="mt-4 rounded-xl bg-white/[0.04] ring-1 ring-white/10">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold">{open ? '▾' : '▸'} Music &amp; look</span>
        <span className="text-xs text-white/50">
          {music} · {look}
        </span>
        <span className="ml-auto text-xs text-white/40">{open ? 'Close' : 'Change'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 sm:px-5 sm:pb-5">
          <p className="mb-4 px-2 text-xs text-white/45 sm:px-0">Saved as you choose. The music goes softer while the voice speaks.</p>
          <MusicLookFields state={state} suggested={suggested} looks={looks} coverUrl={coverUrl} />
        </div>
      )}
    </div>
  );
}
